import type { ReportMetadata } from '../types';
import type {
    ForecastInput,
    MonthSeries,
    WorkloadPriceRange,
    WorkloadSettings,
} from './cockpitTypes';
import type {
    ForecastIntervalConfig,
    ForecastLogEntry,
    ForecastMetadata,
    ForecastProject,
    ForecastServiceRow,
} from './cockpitForecast';
import {
    altQuantity,
    calendarDay,
    daysInMonth,
    isMigrationBulkBackfill,
    isWorkEntry,
    performedDateIso,
    performedMonth,
} from './cockpitLogic';
import {
    isFlatBookingReference,
    isFlatRateService,
    resolveFlatCoverage,
    type FlatCoverageServiceLike,
} from './flatRateLogic';
import { roundCents } from './money';
import { inspectionProofMatcher } from './projectLv';
import { contractedQuantityOfRow } from './nepPosition';
import { getAusfuehrungsVertreter } from './projectRepresentatives';
import { isBackfilledAltEntry } from './serviceLogUtils';
import { isDailyUnit, isHourlyUnit, isMonthlyUnit, isWeeklyUnit } from './serviceUnits';
import { istUebungsprojekt } from './uebung/uebungService';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AUSLASTUNG DES KPI-COCKPITS — Ist und Plan je Person und Monat
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * REGEL (Betreiber, verbindlich): Auslastung je Person und Monat =
 * max(IST, PLAN).
 *
 *  - IST: gebuchte Arbeit aus dem Protokoll, dem AUSFUEHRENDEN zugerechnet,
 *    fuer die Monate bis einschliesslich des laufenden. Tagessaetze zaehlen
 *    8 h, Stunden 1:1, Stueck nach `minutesForPosition`.
 *  - PLAN: Termine aus Turnus und Wochentagen ab dem laufenden Monat, OHNE
 *    Deckelung durch die Restmenge (ein Ganztagskoordinator arbeitet weiter,
 *    auch wenn das LV aufgebraucht ist — das ist ein Nachtrag, keine freie
 *    Kapazitaet). Termine an Wochentagen eines Stellvertreters gehen an ihn.
 *
 * Der Geld-Forecast (cockpitForecast) bleibt davon unberuehrt: dort gelten
 * Restmenge und Enddatum weiter.
 *
 * REINE FUNKTIONEN: `today` und `year` kommen als Parameter.
 */

const MONTHS = 12;
const zeros = (): MonthSeries => [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

/** Minuten eines Arbeitstags / einer Arbeitswoche. */
export const DAY_MINUTES = 480;
export const WEEK_MINUTES = 5 * DAY_MINUTES;
const HALF_DAY_MINUTES = 240;

const num = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

/* ═════════════════════════════════════════════════════════════════════════
 * 1. Minuten je Stueck einer Position
 * ═══════════════════════════════════════════════════════════════════════ */

/** Die Felder einer Position, die `minutesForPosition` liest. */
export interface WorkloadRowLike {
    assignment?: unknown;
    title?: unknown;
    unit_price?: unknown;
}

const HALF_DAY_TITLE = /halbtag|bis\s*4\s*st/i;
const FULL_DAY_TITLE = /tagespauschale|ganztag|bis\s*8\s*st/i;

/** Staffelwert zum Preis — exakter Treffer, sonst die hoechste Stufe, deren `min` erreicht ist. */
const priceRangeMinutes = (ranges: WorkloadPriceRange[] | undefined, price: number): number => {
    if (!Array.isArray(ranges) || ranges.length === 0 || price <= 0) return 0;
    const valid = ranges.filter((r) => r && Number.isFinite(Number(r.min)) && num(r.minutes) > 0);
    const exact = valid.find((r) => price >= num(r.min) && price <= num(r.max));
    if (exact) return num(exact.minutes);
    // Luecke zwischen zwei Stufen (250 < 250,50 < 251): die naechstniedrigere Stufe.
    const below = valid.filter((r) => num(r.min) <= price).sort((a, b) => num(b.min) - num(a.min))[0];
    return below ? num(below.minutes) : 0;
};

/**
 * Minuten je STUECK einer Position — die EINE Regel fuer Ist und Plan.
 *
 *  (a) ausdruecklich gepflegte Minuten der Zuordnung (`assignment_minutes`);
 *  (b) Titelhinweis: „Halbtag“/„bis 4 Std“ = 240, „Tagespauschale“/„Ganztag“/
 *      „bis 8 Std“ = 480 — der Titel schlaegt den Preis (eine Tagespauschale zu
 *      880 € ist ein Arbeitstag, keine 600-Minuten-Preisstufe);
 *  (c) Preisstaffel auf dem Einzelpreis DIESER Zeile (alte `gM`-Regel);
 *  (d) der Firmen-Default (`minutesPerInspection.default`).
 *
 * Halbtag wird VOR Tagespauschale geprueft: „Halbtagspauschale“ enthaelt
 * „tagespauschale“.
 */
export const minutesForPosition = (row: WorkloadRowLike | null | undefined, workload: WorkloadSettings | null | undefined): number => {
    const table = workload?.minutesPerInspection;
    const assignment = (typeof row?.assignment === 'string' && row.assignment) || 'additional';
    if (table && assignment !== 'default' && Object.prototype.hasOwnProperty.call(table, assignment)) {
        const named = num(table[assignment]);
        if (named > 0) return named;
    }
    const title = typeof row?.title === 'string' ? row.title : '';
    if (HALF_DAY_TITLE.test(title)) return HALF_DAY_MINUTES;
    if (FULL_DAY_TITLE.test(title)) return DAY_MINUTES;
    const byPrice = priceRangeMinutes(workload?.priceRanges, num(row?.unit_price));
    if (byPrice > 0) return byPrice;
    const fallback = num(table?.default);
    return fallback > 0 ? fallback : 0;
};

/**
 * Minuten fuer eine MENGE dieser Position: die Einheit geht vor —
 * Stunden 1:1, Tage 8 h, Wochen 40 h, sonst Menge × `minutesForPosition`.
 */
export const minutesForQuantity = (
    row: (WorkloadRowLike & { unit?: unknown }) | null | undefined,
    quantity: number,
    workload: WorkloadSettings | null | undefined,
): number => {
    if (!(quantity > 0)) return 0;
    const unit = typeof row?.unit === 'string' ? row.unit : '';
    if (isHourlyUnit(unit)) return quantity * 60;
    if (isDailyUnit(unit)) return quantity * DAY_MINUTES;
    if (isWeeklyUnit(unit)) return quantity * WEEK_MINUTES;
    return quantity * minutesForPosition(row, workload);
};

/* ═════════════════════════════════════════════════════════════════════════
 * 2. Kalender auf Tagesnummern (wie cockpitForecast, zeitzonenfrei)
 * ═══════════════════════════════════════════════════════════════════════ */

const dayNumber = (y: number, m: number, d: number): number => {
    const month = m + 1;
    const shifted = y - (month <= 2 ? 1 : 0);
    const era = Math.floor(shifted / 400);
    const yearOfEra = shifted - era * 400;
    const dayOfYear = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + d - 1;
    const dayOfEra = yearOfEra * 365 + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100) + dayOfYear;
    return era * 146097 + dayOfEra - 719468;
};

/** Wochentag einer Tagesnummer, 0 = Sonntag wie `Date.getDay()` (1970-01-01 war ein Donnerstag). */
const weekdayOf = (n: number): number => (((n + 4) % 7) + 7) % 7;

/** Monat (0–11) einer Tagesnummer innerhalb von `year`, sonst -1. */
const monthOfDay = (n: number, year: number): number => {
    for (let m = MONTHS - 1; m >= 0; m -= 1) {
        if (n >= dayNumber(year, m, 1)) return n <= dayNumber(year, m, daysInMonth(year, m)) ? m : -1;
    }
    return -1;
};

const validWeekdays = (days: unknown): number[] =>
    Array.isArray(days)
        ? [...new Set(days.map((d) => Number(d)).filter((d) => Number.isInteger(d) && d >= 0 && d <= 6))]
        : [];

/* ═════════════════════════════════════════════════════════════════════════
 * 3. Termine eines Turnus — konkrete Tage, damit der Wochentag bekannt ist
 * ═══════════════════════════════════════════════════════════════════════ */

export interface AppointmentWindow {
    /** Erster Tag (Tagesnummer), einschliesslich. */
    from: number;
    /** Letzter Tag (Tagesnummer), einschliesslich. */
    to: number;
    /** Anker des Turnus (Projektbeginn) — fuer Schrittlogik und Zweiwochen-Paritaet. */
    anchor: number;
}

const stepDays = (interval: ForecastIntervalConfig | null | undefined): number => {
    const type = interval?.type;
    if (type === 'custom') return Math.max(1, Math.round(num(interval?.customDays) || 7));
    if (type === 'daily') return 1;
    if (type === 'biweekly') return 14;
    if (type === 'twice_monthly') return 15;
    if (type === 'monthly') return 30;
    return 7;
};

/**
 * Wochentage eines `custom`-Turnus mit 1–7 Terminen JE WOCHE ohne eigene
 * Wochentage — wortgleich `dateService.getWeekdaysFromFrequency` (dort nicht
 * exportiert). Dieselbe Tabelle, damit Terminplan und Auslastung dieselben
 * Tage meinen.
 */
export const weekdaysFromFrequency = (freq: number): number[] => {
    if (freq <= 0) return [3];
    if (freq >= 7) return [0, 1, 2, 3, 4, 5, 6];
    const patterns: Record<number, number[]> = {
        1: [3], 2: [2, 4], 3: [1, 3, 5], 4: [1, 2, 4, 5], 5: [1, 2, 3, 4, 5], 6: [1, 2, 3, 4, 5, 6],
    };
    return patterns[freq] || [3];
};

const mondayOf = (n: number): number => n - ((weekdayOf(n) + 6) % 7);

/** Schluessel Monat (oder Monatshaelfte) einer Tagesnummer, ohne Date. */
const halfMonthKey = (n: number, half: boolean): string => {
    // Ruecklauf ueber die Tagesnummer: Jahr grob schaetzen, dann Monat suchen.
    let year = Math.floor(n / 365.2425) + 1970;
    while (dayNumber(year, 0, 1) > n) year -= 1;
    while (dayNumber(year + 1, 0, 1) <= n) year += 1;
    const month = monthOfDay(n, year);
    const dom = n - dayNumber(year, month, 1) + 1;
    return `${year}-${month}${half ? (dom <= 15 ? 'a' : 'b') : ''}`;
};

/**
 * Die Termintage eines Turnus im Fenster.
 *
 * MIT WOCHENTAGEN (`weekdays` nicht leer): jeder passende Wochentag bei
 * `weekly`/`daily`/ohne Turnus; `biweekly` jede zweite Woche ab dem Anker;
 * `twice_monthly` der erste passende Tag der ersten und der zweiten
 * Monatshaelfte; `monthly` der erste passende Tag des Monats.
 *
 * `custom` WIE dateService.calculateNextDueDate: `customDays` 1–7 (ungleich 0)
 * heisst so viele Termine JE WOCHE — an den eigenen Wochentagen, sonst an den
 * Tagen aus `weekdaysFromFrequency`, und dann an JEDEM dieser Wochentage;
 * `customDays` > 7 oder leer ist ein Tagesschritt (leer = 7).
 *
 * OHNE WOCHENTAGE: die Schrittlogik ab dem Anker (wie der Geld-Forecast).
 */
export const appointmentDays = (
    interval: ForecastIntervalConfig | null | undefined,
    weekdays: number[],
    window: AppointmentWindow,
): number[] => {
    const out: number[] = [];
    if (window.to < window.from) return out;
    const type = interval?.type;
    let days = validWeekdays(weekdays);
    if (type === 'custom') {
        const customDays = num(interval?.customDays);
        const perWeek = customDays !== 0 && customDays <= 7;
        days = perWeek ? (days.length > 0 ? days : weekdaysFromFrequency(customDays)) : [];
    }
    if (days.length > 0) {
        const wanted = new Set(days);
        if (type === 'monthly' || type === 'twice_monthly') {
            // Je Kalendermonat (bzw. Monatshaelfte) der erste passende Tag.
            let bucket = '';
            for (let n = window.from; n <= window.to; n += 1) {
                if (!wanted.has(weekdayOf(n))) continue;
                const key = halfMonthKey(n, type === 'twice_monthly');
                if (key === bucket) continue;
                bucket = key;
                out.push(n);
            }
            return out;
        }
        for (let n = window.from; n <= window.to; n += 1) {
            if (!wanted.has(weekdayOf(n))) continue;
            if (type === 'biweekly') {
                // Wochenparitaet ab der Woche des Ankers (Wochen ab Montag).
                const week = Math.floor((n - mondayOf(window.anchor)) / 7);
                if (((week % 2) + 2) % 2 !== 0) continue;
            }
            out.push(n);
        }
        return out;
    }
    const step = stepDays(interval);
    const first = Math.max(0, Math.ceil((window.from - window.anchor) / step));
    for (let k = first; ; k += 1) {
        const n = window.anchor + k * step;
        if (n > window.to) break;
        if (n >= window.from) out.push(n);
    }
    return out;
};

/**
 * Zahl der Termine je Monat — die pruefbare Kurzform von `appointmentDays`
 * fuer ein Fenster, das in `year` liegt.
 */
export const appointmentsPerMonth = (
    interval: ForecastIntervalConfig | null | undefined,
    weekdays: number[],
    window: AppointmentWindow,
    year: number,
): MonthSeries => {
    const out = zeros();
    for (const n of appointmentDays(interval, weekdays, window)) {
        const m = monthOfDay(n, year);
        if (m >= 0) out[m] += 1;
    }
    return out;
};

/* ═════════════════════════════════════════════════════════════════════════
 * 4. Gemeinsame Projektlesung
 * ═══════════════════════════════════════════════════════════════════════ */

interface UserLike { id?: string | null; first_name?: string | null; last_name?: string | null; }

const nameKey = (value: unknown): string =>
    (typeof value === 'string' ? value : '').replace(/\s+/g, ' ').trim().toLowerCase();

interface People {
    ids: Set<string>;
    byName: Map<string, string>;
}

const peopleOf = (users: UserLike[] | null | undefined): People => {
    const ids = new Set<string>();
    const byName = new Map<string, string>();
    for (const user of users || []) {
        const id = user?.id;
        if (typeof id !== 'string' || !id) continue;
        ids.add(id);
        const key = nameKey(`${user.first_name ?? ''} ${user.last_name ?? ''}`);
        if (key && !byName.has(key)) byName.set(key, id);
    }
    return { ids, byName };
};

/** Verantwortlich = Koordinator (bekannt) ?? Eigentuemer (bekannt) ?? 'unassigned' — wie Engine und Forecast. */
const responsibleOf = (project: ForecastProject, metadata: ForecastMetadata | null | undefined, people: People): string => {
    const coordinator = metadata?.coordinatorId;
    if (coordinator && people.ids.has(coordinator)) return coordinator;
    const owner = project.user_id;
    if (owner && people.ids.has(owner)) return owner;
    return UNASSIGNED_ID;
};

/** Dieselbe Kennung wie `cockpitEngine.UNASSIGNED` (ein Import waere ein Zyklus; ein Test haelt beide gleich). */
export const UNASSIGNED_ID = 'unassigned';

const statusOf = (project: ForecastProject, metadata: ForecastMetadata | null | undefined): string =>
    project.status || metadata?.globalData?.status || 'planning';

/** Dieselbe Uebersetzung wie `cockpitForecast.flatLikeRow` (Spalte `flat_rate` oder Blob `flatRate`). */
const flatLike = (row: ForecastServiceRow, id: string): FlatCoverageServiceLike => {
    const raw = row.flat_rate ?? row.flatRate;
    return {
        id,
        assignment: row.assignment ?? undefined,
        lvPosition: row.lv_position ?? row.lvPosition ?? undefined,
        title: row.title ?? undefined,
        flatRate: raw && typeof raw === 'object' ? raw : undefined,
    };
};

const isFlatRow = (row: ForecastServiceRow, id: string): boolean =>
    isFlatRateService(flatLike(row, id)) || isMonthlyUnit(row.unit);

const isInspection = (row: ForecastServiceRow): boolean => (row.assignment || 'additional') === 'inspection';

const ensure = (bucket: Record<string, MonthSeries>, key: string): MonthSeries => {
    const found = bucket[key];
    if (found) return found;
    const created = zeros();
    bucket[key] = created;
    return created;
};

/** Grenzen relativ zu `today`: letzter Ist-Monat und erster Plan-Monat im Jahr. */
export const workloadBoundaries = (year: number, today: string | null | undefined): { lastIstMonth: number; firstPlanMonth: number } => {
    const t = calendarDay(today);
    if (!t) return { lastIstMonth: -1, firstPlanMonth: 0 };
    if (t.year < year) return { lastIstMonth: -1, firstPlanMonth: 0 };
    if (t.year > year) return { lastIstMonth: 11, firstPlanMonth: MONTHS };
    return { lastIstMonth: t.month, firstPlanMonth: t.month };
};

/* ═════════════════════════════════════════════════════════════════════════
 * 5. IST — gebuchte Arbeit nach Ausfuehrendem
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * Wer hat es ausgefuehrt? `performerId` (bekannt), sonst der Name (auch fuer
 * `system`-Stubs). Ein AUSDRUECKLICH genannter, aber unbekannter Ausfuehrender
 * (fremde oder ausgeschiedene Person) landet bei 'unassigned' — seine Stunden
 * gehoeren nicht dem Koordinator. Nur ein Eintrag OHNE Ausfuehrenden-Id (bzw.
 * ein unaufloesbarer `system`-Stub) faellt auf den Projektverantwortlichen.
 */
const performerOf = (log: ForecastLogEntry, people: People, responsible: string): string => {
    const raw = (log as { performerId?: unknown }).performerId;
    const id = typeof raw === 'string' ? raw.trim() : '';
    if (id && id !== 'system' && people.ids.has(id)) return id;
    const byName = people.byName.get(nameKey(log.performerName));
    if (byName) return byName;
    return id && id !== 'system' ? UNASSIGNED_ID : responsible;
};

/**
 * IST-Minuten je Person und Monat aus den Protokollen ALLER Projekte (nicht
 * nur der aktiven: gearbeitet ist gearbeitet), Monate bis einschliesslich des
 * laufenden.
 *
 * Arbeitseintraege (`isWorkEntry`), ohne Migrations-Sammelbuchung, ALT-Zeilen
 * mit ihrer angewandten Menge (`altQuantity`). Pauschalmonate (`FLAT-` bzw.
 * eine Pauschalposition) zaehlen 0 — die gedeckten Begehungen tragen die
 * Arbeit. Zugerechnet wird dem AUSFUEHRENDEN: `performerId` (bekannt), sonst
 * der Name (auch fuer `system`-Stubs), sonst der Projektverantwortliche.
 *
 * NACHWEISZEILEN (Menge 0, 'NW-…' auf der Pauschale): seit 30.09.2026 steht
 * die Begehung im Pauschalprojekt dort und nicht mehr auf der
 * Begehungsposition. Sie ist ein Termin — mit den Minuten eines Termins der
 * Begehungsposition, die auch den Plan traegt (appointmentMinutes). Ohne das
 * fiele die gesamte Begehungsarbeit dieser Projekte ab der Umstellung aus dem
 * Ist.
 */
export const buildWorkloadIst = (input: ForecastInput): Record<string, MonthSeries> => {
    const year = Math.trunc(num(input?.year));
    const out: Record<string, MonthSeries> = {};
    const { lastIstMonth } = workloadBoundaries(year, input?.today);
    if (lastIstMonth < 0) return out;
    const people = peopleOf(input?.users as UserLike[]);
    const projects: ForecastProject[] = input?.projects || [];

    for (const project of projects) {
        if (!project || istUebungsprojekt(project)) continue;
        const metadata = project.data?.metadata ?? null;
        const logs: ForecastLogEntry[] = metadata?.globalData?.serviceLog || [];
        if (logs.length === 0) continue;
        const rows = new Map<string, ForecastServiceRow>();
        for (const row of project.project_services || []) {
            if (row && typeof row.id === 'string' && row.id && !rows.has(row.id)) rows.set(row.id, row);
        }
        const responsible = responsibleOf(project, metadata, people);
        const isProof = inspectionProofMatcher([...rows.values()]);
        let proofMinutes: number | null = null;
        const minutesPerProof = (): number => {
            if (proofMinutes === null) {
                const candidates = [...rows.entries()]
                    .map(([serviceId, row]) => ({ row, serviceId }))
                    .filter(({ row, serviceId }) => isInspection(row) && !isFlatRow(row, serviceId));
                proofMinutes = appointmentMinutes(primaryInspectionRow(candidates, logs, input?.today), logs, input.workload);
            }
            return proofMinutes;
        };

        for (const log of logs) {
            const serviceId = typeof log?.serviceId === 'string' ? log.serviceId : '';
            if (!serviceId || serviceId === 'contract_change') continue;
            if (!isWorkEntry(log) || isMigrationBulkBackfill(log)) continue;
            if (isFlatBookingReference(log.bookingReference)) continue;
            const month = performedMonth(log, year);
            if (month === null || month > lastIstMonth) continue;
            if (isProof(log)) {
                const minutes = minutesPerProof();
                if (Number.isFinite(minutes) && minutes > 0) ensure(out, performerOf(log, people, responsible))[month] += minutes;
                continue;
            }
            // NUR ENDLICHE MENGEN: ein "Infinity" aus dem jsonb besteht die
            // Pruefung `> 0` und vergiftete sonst den ganzen Monat.
            const quantity = altQuantity(log);
            if (!Number.isFinite(quantity) || quantity <= 0) continue;
            const row = rows.get(serviceId);
            if (!row || isFlatRow(row, serviceId)) continue;
            const minutes = minutesForQuantity(row, quantity, input.workload);
            if (!Number.isFinite(minutes) || minutes <= 0) continue;
            ensure(out, performerOf(log, people, responsible))[month] += minutes;
        }
    }
    for (const series of Object.values(out)) {
        for (let m = 0; m < MONTHS; m += 1) series[m] = Math.round(series[m]);
    }
    return out;
};

/* ═════════════════════════════════════════════════════════════════════════
 * 6. PLAN — Termine aus Turnus und Wochentagen
 * ═══════════════════════════════════════════════════════════════════════ */

/** Stunden je Termin einer Stunden-Begehung: Schnitt der echten Eintraege, sonst 1. */
const hoursPerAppointment = (logs: ForecastLogEntry[], serviceId: string): number => {
    let sum = 0;
    let count = 0;
    for (const log of logs) {
        if (!log || log.serviceId !== serviceId || !isWorkEntry(log)) continue;
        if (isMigrationBulkBackfill(log) || isBackfilledAltEntry(log)) continue;
        const qty = altQuantity(log);
        if (!Number.isFinite(qty) || qty <= 0) continue;
        sum += qty;
        count += 1;
    }
    return count > 0 && sum > 0 ? sum / count : 1;
};

/**
 * Die EINE Begehungsposition, die die Termine eines Projekts traegt.
 *
 * Tages- und Halbtagspauschale sind Alternativen fuer DENSELBEN Termin, nicht
 * zwei Termine. Massgeblich ist die Position mit der meisten gebuchten Menge
 * der letzten 90 Tage; ohne solche Buchung die erste Begehungsposition.
 */
export const primaryInspectionRow = (
    candidates: { row: ForecastServiceRow; serviceId: string }[],
    logs: ForecastLogEntry[],
    today: string | null | undefined,
): { row: ForecastServiceRow; serviceId: string } | null => {
    if (candidates.length === 0) return null;
    const t = calendarDay(today);
    if (!t) return candidates[0];
    const end = dayNumber(t.year, t.month, t.day);
    const start = end - 90;
    const share = new Map<string, number>();
    for (const log of logs) {
        if (!log || typeof log.serviceId !== 'string' || !isWorkEntry(log) || isMigrationBulkBackfill(log)) continue;
        const day = calendarDay(performedDateIso(log));
        if (!day) continue;
        const n = dayNumber(day.year, day.month, day.day);
        if (n < start || n > end) continue;
        const qty = altQuantity(log);
        if (Number.isFinite(qty) && qty > 0) share.set(log.serviceId, (share.get(log.serviceId) || 0) + qty);
    }
    let best = candidates[0];
    let bestShare = share.get(best.serviceId) || 0;
    for (const entry of candidates) {
        const s = share.get(entry.serviceId) || 0;
        if (s > bestShare) {
            best = entry;
            bestShare = s;
        }
    }
    return best;
};

/**
 * Minuten EINES Termins der Begehungsposition, die die Termine traegt — die
 * eine Regel fuer den Plan und fuer die Nachweiszeilen im Ist.
 *
 * Ohne Begehungsposition (reines Pauschal-LV) der Wert der Zuordnung
 * 'inspection' bzw. der Firmen-Default: ein Nachweis ist trotzdem ein Termin.
 */
const appointmentMinutes = (
    primary: { row: ForecastServiceRow; serviceId: string } | null,
    logs: ForecastLogEntry[],
    workload: WorkloadSettings | null | undefined,
): number => {
    if (!primary) return minutesForPosition({ assignment: 'inspection' }, workload);
    const unit = primary.row.unit || '';
    if (isHourlyUnit(unit)) return hoursPerAppointment(logs, primary.serviceId) * 60;
    if (isDailyUnit(unit)) return DAY_MINUTES;
    if (isWeeklyUnit(unit)) return WEEK_MINUTES;
    return minutesForPosition(primary.row, workload);
};

/** Verteilt eine ganze Zahl gleichmaessig ueber `count` Monate ab `startAbs`, nur der Anteil in `year`. */
const spreadEvenly = (total: number, startAbs: number, count: number, year: number, into: MonthSeries): void => {
    if (count <= 0 || total === 0) return;
    const yearStart = year * MONTHS;
    let previous = 0;
    for (let i = 0; i < count; i += 1) {
        const cumulative = roundCents((total * (i + 1)) / count);
        const share = cumulative - previous;
        previous = cumulative;
        const index = startAbs + i - yearStart;
        if (index >= 0 && index < MONTHS) into[index] += share;
    }
};

/**
 * PLAN-Minuten je Person und Monat — nur aktive Projekte, ab dem laufenden
 * Monat (der laufende Monat ganz, zum Vergleich mit dem Ist).
 *
 *  - Begehungen: Termine aus Turnus/Wochentagen × Minuten der tragenden
 *    Position (`primaryInspectionRow`); NICHT gedeckelt durch die Restmenge.
 *    Ein ueberschrittenes Enddatum beendet die Planung nicht, solange das
 *    Projekt `active` ist (dann bis Jahresende). Termine an Wochentagen eines
 *    Stellvertreters gehen an ihn.
 *  - Pauschalen: deckt die Pauschale Begehungen, tragen diese die Arbeit (0);
 *    sonst Minuten je Monat.
 *  - Sonstige: Restmenge × Einheitenminuten, gleichmaessig ueber die
 *    verbleibenden Projektmonate.
 */
export const buildWorkloadPlan = (input: ForecastInput): Record<string, MonthSeries> => {
    const year = Math.trunc(num(input?.year));
    const out: Record<string, MonthSeries> = {};
    const { firstPlanMonth } = workloadBoundaries(year, input?.today);
    if (firstPlanMonth >= MONTHS) return out;
    const people = peopleOf(input?.users as UserLike[]);
    const workload = input?.workload;
    const t = calendarDay(input?.today);
    const todayNumber = t ? dayNumber(t.year, t.month, t.day) : null;
    const planFrom = dayNumber(year, firstPlanMonth, 1);
    const decemberLast = dayNumber(year, 11, 31);

    for (const project of input?.projects || []) {
        if (!project || istUebungsprojekt(project)) continue;
        const metadata = (project as ForecastProject).data?.metadata ?? null;
        if (statusOf(project, metadata) !== 'active') continue;
        const global = metadata?.globalData ?? null;
        const logs: ForecastLogEntry[] = global?.serviceLog || [];

        const start = calendarDay(project.start_date) ?? calendarDay(global?.startDate)
            ?? calendarDay(project.created_at) ?? { year, month: 0, day: 1 };
        const startNumber = dayNumber(start.year, start.month, start.day);
        const end = calendarDay(project.end_date) ?? calendarDay(global?.endDate);
        const endNumber = end ? dayNumber(end.year, end.month, end.day) : null;
        // UEBERFAELLIG, ABER AKTIV: die Baustelle laeuft sichtbar weiter.
        const overdue = endNumber !== null && todayNumber !== null && endNumber < todayNumber;
        const effectiveEnd = endNumber === null || overdue ? decemberLast : Math.min(endNumber, decemberLast);
        const from = Math.max(startNumber, planFrom);
        if (from > effectiveEnd) continue;

        const responsible = responsibleOf(project, metadata, people);
        const serviceRows = (project.project_services || [])
            .filter((row): row is ForecastServiceRow => !!row && typeof row.id === 'string' && row.id !== '')
            .map((row) => ({ row, serviceId: row.id as string }));
        const seen = new Set<string>();
        const unique = serviceRows.filter(({ serviceId }) => (seen.has(serviceId) ? false : (seen.add(serviceId), true)));
        const coverage = resolveFlatCoverage(unique.map(({ row, serviceId }) => flatLike(row, serviceId)));

        const usedAll = new Map<string, number>();
        for (const log of logs) {
            if (!log || typeof log.serviceId !== 'string' || !isWorkEntry(log)) continue;
            const qty = altQuantity(log);
            if (Number.isFinite(qty) && qty > 0) usedAll.set(log.serviceId, (usedAll.get(log.serviceId) || 0) + qty);
        }

        // ── Begehungen ──────────────────────────────────────────────────
        const inspectionCandidates = unique.filter(
            ({ row, serviceId }) => isInspection(row) && !isFlatRow(row, serviceId)
                && (contractedQuantityOfRow(row) > 0 || (usedAll.get(serviceId) || 0) > 0),
        );
        const primary = primaryInspectionRow(inspectionCandidates, logs, input?.today);
        if (primary) {
            const perAppointment = appointmentMinutes(primary, logs, workload);
            const interval = metadata?.intervalConfig ?? null;
            const ownerDays = validWeekdays(interval?.preferredWeekdays);
            // Nur Ausfuehrungs-Vertreter: Die Verantwortliche Planungsphase
            // macht keine Begehungen. Ihre (Alt-)Wochentage duerfen weder
            // Termine erzeugen noch Begehungsminuten auf sie buchen.
            const deputies = getAusfuehrungsVertreter(metadata as unknown as ReportMetadata)
                .slice(1)
                .filter((rep) => !!rep.id && people.ids.has(rep.id) && validWeekdays(rep.weekdays).length > 0);
            const deputyDays = deputies.flatMap((rep) => validWeekdays(rep.weekdays));
            // Wochentage des Projekts = Eigentuemertage ∪ Vertretertage (die
            // Oberflaeche teilt die Woche zwischen ihnen auf) — auch wenn der
            // Eigentuemer selbst keinen Tag hat. Erst wenn niemand einen
            // Wochentag traegt, gilt die Schrittlogik.
            const weekdays = [...new Set([...ownerDays, ...deputyDays])];
            // ANKER: der naechste geplante Termin, sonst die letzte Begehung,
            // sonst der Projektbeginn — sonst laege die Zweiwochen-Paritaet
            // (und der Tagesschritt) gegen den echten Terminplan versetzt.
            const anchorDay = calendarDay(interval?.nextScheduledDate) ?? calendarDay(interval?.lastInspectionDate);
            const anchor = anchorDay ? dayNumber(anchorDay.year, anchorDay.month, anchorDay.day) : startNumber;
            const days = appointmentDays(interval, weekdays, { from, to: effectiveEnd, anchor });
            for (const n of days) {
                const m = monthOfDay(n, year);
                if (m < 0) continue;
                const dow = weekdayOf(n);
                const deputy = deputies.find((rep) => validWeekdays(rep.weekdays).includes(dow));
                if (Number.isFinite(perAppointment) && perAppointment > 0) {
                    ensure(out, deputy?.id ?? responsible)[m] += perAppointment;
                }
            }
        }

        // ── Pauschalen und Sonstige ─────────────────────────────────────
        const coveringFlats = new Set(coverage.values());
        const hasPlannedInspections = !!primary;
        const firstMonth = monthOfDay(from, year);
        const lastMonth = monthOfDay(effectiveEnd, year);
        for (const { row, serviceId } of unique) {
            if (isInspection(row) && !isFlatRow(row, serviceId)) continue;
            if (isFlatRow(row, serviceId)) {
                if (coveringFlats.has(serviceId) && hasPlannedInspections) continue;
                const perMonth = minutesForPosition(row, workload);
                if (!Number.isFinite(perMonth) || perMonth <= 0 || firstMonth < 0 || lastMonth < 0) continue;
                const series = ensure(out, responsible);
                for (let m = firstMonth; m <= lastMonth; m += 1) series[m] += perMonth;
                continue;
            }
            const contracted = contractedQuantityOfRow(row);
            if (contracted <= 0) continue;
            const remaining = Math.max(0, contracted - (usedAll.get(serviceId) || 0));
            const minutes = Math.round(minutesForQuantity(row, remaining, workload));
            if (!Number.isFinite(minutes) || minutes <= 0) continue;
            const startAbs = year * MONTHS + Math.max(0, firstMonth);
            const endAbs = end && !overdue ? Math.max(startAbs, end.year * MONTHS + end.month) : year * MONTHS + 11;
            spreadEvenly(minutes, startAbs, endAbs - startAbs + 1, year, ensure(out, responsible));
        }
    }
    for (const series of Object.values(out)) {
        for (let m = 0; m < MONTHS; m += 1) series[m] = Math.round(series[m]);
    }
    return out;
};

/** Monatsweise max(Ist, Plan) samt Quelle je Monat. */
export const combineWorkload = (
    ist: MonthSeries | null | undefined,
    plan: MonthSeries | null | undefined,
): { minutes: MonthSeries; source: ('ist' | 'plan' | null)[] } => {
    const minutes = zeros();
    const source: ('ist' | 'plan' | null)[] = new Array(MONTHS).fill(null);
    for (let m = 0; m < MONTHS; m += 1) {
        const i = num(ist?.[m]);
        const p = num(plan?.[m]);
        minutes[m] = Math.max(i, p);
        source[m] = minutes[m] <= 0 ? null : i >= p ? 'ist' : 'plan';
    }
    return { minutes, source };
};
